/**
 * MarcaPage — full-page route for /admin/marca.
 *
 * Visual: two-column layout on the app's paper background (#f4f3ee / --background).
 *   LEFT  (~5/12): white card, rounded-[24px], underline-style inputs, round color
 *          swatches, dashed logo dropzone, pill action buttons.
 *   RIGHT (~7/12, sticky): live document mockup reflecting real brand data, info box.
 *
 * Business logic: identical to MarcaTab — reads via getMarca(), saves via
 * updateMarca() + applyBrandTheme() + toast. Logo upload via uploadImagen().
 */
import { useEffect, useRef, useState } from "react";
import { CloudUpload, Save, Check, Info, Loader2, Upload, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { themeFromMarca } from "@marco/pdf";
import { applyBrandTheme } from "@/shared/lib/brand-theme.js";
import { friendlyMessage } from "@/shared/lib/errors.js";
import { cn } from "@/shared/lib/utils.js";
import { buttonVariants } from "@/shared/ui/button.js";
import type { Marca } from "../types.js";
import type { Informe } from "../../reports/types.js";
import { getMarcaForEdit, updateMarca } from "../services/marca.service.js";
import { uploadImagen } from "@/shared/lib/upload.js";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const BRAND_VACIO: Marca = {
  nombreComercial: "",
  tagline: "",
  direccion: "",
  telefonos: "",
  correo: "",
  sitioWeb: "",
  colorPrimario: "#1a1a18",
  colorAcento: "#f5a623",
  logoUrl: "",
  logoUrlClaro: "",
};

/**
 * Sample IE (Informe de Evaluación) used by the brand preview — the simplest,
 * most complete template. Filled with lorem ipsum so the entire PDF renders;
 * colors and logo come from the live brand state.
 */
const SAMPLE_INFORME: Informe = {
  id: "brand-preview",
  tipo: "IE",
  numero: "IE2026-001",
  cliente: "Lorem Ipsum S.A.C.",
  clienteId: null,
  estado: "borrador",
  autorId: "preview",
  parentId: null,
  pdfUrl: null,
  createdAt: "2026-01-01T00:00:00Z",
  updatedAt: "2026-01-01T00:00:00Z",
  payload: {
    datosGenerales: {
      cliente: "Lorem Ipsum S.A.C.",
      atencion: "Lorem Dolor",
      supervisor: "Sit Amet",
      cargo: "Jefe de planta",
      anexo: "01",
      revision: 0,
      fechaInicio: "2026-01-01",
      fechaCierre: "2026-01-02",
      emision: "2026-01-03",
      referencia: "Lorem ipsum dolor sit amet",
      lugar: "Consectetur adipiscing",
      ocCliente: "OC-123",
      otMpsa: "OT-456",
    },
    equipos: [
      {
        item: 1,
        equipo: "Lorem ipsum dolor",
        marca: "SKF",
        modelo: "6205-2RS",
        serie: "SN-001",
        designacion: "Eje principal",
      },
      {
        item: 2,
        equipo: "Consectetur adipiscing",
        marca: "WEG",
        modelo: "W22 7.5HP",
        serie: "SN-002",
        designacion: "Motor auxiliar",
      },
      {
        item: 3,
        equipo: "Sed ut perspiciatis",
        marca: "FAG",
        modelo: "32207-A",
        serie: "SN-003",
        designacion: "Rodamiento cónico",
      },
    ],
    panelFotografico: [
      {
        url: "",
        titulo: "Inspección inicial",
        descripcion: "Lorem ipsum dolor sit amet, consectetur adipiscing.",
      },
      {
        url: "",
        titulo: "Proceso de servicio",
        descripcion: "Sed do eiusmod tempor incididunt ut labore et dolore.",
      },
    ],
    problemas: [
      {
        descripcion:
          "Lorem ipsum: la unidad presenta desgaste en el sello principal y fuga leve de lubricante.",
        recomendacion:
          "Reemplazar el sello principal y programar mantenimiento preventivo a 500 horas.",
      },
      {
        descripcion:
          "Consectetur: vibración fuera de rango en el eje auxiliar durante la operación.",
        recomendacion:
          "Alinear el eje auxiliar y verificar el balanceo dinámico del conjunto.",
      },
    ],
  },
};

const SECTION_LABEL =
  "block font-mono text-[0.625rem] font-bold uppercase tracking-[0.15em] text-[#76746a] mb-3";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function normalizeHex(value: string, fallback: string): string {
  return /^#[0-9a-fA-F]{6}$/.test(value) ? value : fallback;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

/** Underline-style text input — transparent bg, bottom border only. */
function UnderlineInput({
  id,
  label,
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <div>
      <label htmlFor={id} className={SECTION_LABEL}>
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full border-0 border-b border-[#ddd9cc] bg-transparent pb-2 text-sm text-[#1a1a18] outline-none placeholder:text-[#c5c2b8] focus:border-[#1a1a18] transition-colors"
      />
    </div>
  );
}

/** Round color swatch that opens a native color picker on click. */
function ColorSwatch({
  id,
  label,
  value,
  onChange,
  primary,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  /** Primary swatch: larger size + checkmark indicator */
  primary?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const hex = normalizeHex(value, "#1a1a18");

  return (
    <div className="flex flex-col items-center gap-2">
      <button
        type="button"
        title={`Cambiar ${label.toLowerCase()}: ${hex}`}
        aria-label={`Color ${label}: ${hex}. Clic para cambiar`}
        onClick={() => inputRef.current?.click()}
        className={cn(
          "relative rounded-full border-2 border-white shadow-sm transition-transform hover:scale-105 active:scale-95 flex items-center justify-center",
          primary ? "w-12 h-12" : "w-10 h-10",
        )}
        style={{ backgroundColor: hex }}
      >
        {primary && <Check className="text-white size-3.5" aria-hidden strokeWidth={3} />}
      </button>
      <span className="font-mono text-[0.55rem] uppercase tracking-[0.12em] text-[#76746a]">
        {label}
      </span>
      <span className="font-mono text-[0.6rem] text-[#1a1a18] uppercase">{hex}</span>
      <input
        ref={inputRef}
        id={id}
        type="color"
        value={hex}
        onChange={(e) => onChange(e.target.value)}
        className="sr-only"
        aria-label={`Selector de color ${label}`}
      />
    </div>
  );
}

/** Logo area: dashed dropzone when empty, preview + controls when set. */
function LogoField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [subiendo, setSubiendo] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("El archivo debe ser una imagen.");
      return;
    }
    if (file.size > 1_000_000) {
      toast.error("La imagen no debe superar 1 MB.");
      return;
    }
    setSubiendo(true);
    try {
      const url = await uploadImagen(file, "marca");
      onChange(url);
    } catch (err) {
      toast.error(friendlyMessage(err));
    } finally {
      setSubiendo(false);
    }
  }

  if (value) {
    return (
      <div className="flex flex-col gap-3">
        <img
          src={value}
          alt="Logo"
          className="h-32 w-full rounded-xl border border-[#ddd9cc] bg-white object-contain p-3"
        />
        <div className="flex items-center gap-2">
          <label
            className={cn(
              "inline-flex cursor-pointer items-center gap-1.5 rounded-[20px] border border-[#ddd9cc] bg-transparent px-4 py-2 text-xs font-bold text-[#1a1a18] transition-colors hover:bg-[#f4f3ee]",
              subiendo && "pointer-events-none opacity-60",
            )}
          >
            {subiendo ? (
              <Loader2 className="size-3.5 animate-spin" aria-hidden />
            ) : (
              <Upload className="size-3.5" aria-hidden />
            )}
            {subiendo ? "Subiendo…" : "Cambiar"}
            <input
              type="file"
              accept="image/*"
              onChange={onFile}
              disabled={subiendo}
              className="sr-only"
            />
          </label>
          {!subiendo && (
            <button
              type="button"
              onClick={() => onChange("")}
              className="inline-flex items-center gap-1.5 rounded-[20px] border border-[#ddd9cc] bg-transparent px-4 py-2 text-xs font-bold text-[#c0392b] transition-colors hover:bg-[#fdf3f2]"
            >
              Quitar
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        className="w-full rounded-xl border border-dashed border-[#c8c7bf] bg-white p-6 flex flex-col items-center justify-center transition-colors hover:bg-[#f4f3ee] group"
        aria-label="Seleccionar logo"
      >
        <CloudUpload
          className="size-8 text-[#c8c7bf] mb-2 group-hover:text-[#1a1a18] transition-colors"
          aria-hidden
        />
        <p className="text-sm text-[#1a1a18] text-center">
          Arrastre su logo aquí o <span className="font-bold underline">explore</span>
        </p>
        <p className="mt-1.5 font-mono text-[0.6rem] uppercase tracking-[0.12em] text-[#76746a]">
          PNG, JPG (Máx. 1 MB)
        </p>
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={onFile}
        disabled={subiendo}
        className="sr-only"
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PDF preview (right column)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * PdfPreview — renders SAMPLE_INFORME as a real pdfmake PDF in an <iframe>.
 *
 * Lifecycle:
 *   - On mount and whenever relevant brand fields change, regenerates the PDF
 *     debounced at 400ms so dragging the color picker doesn't thrash.
 *   - First render shows a loading spinner; subsequent regenerations keep the
 *     old iframe URL visible until the new PDF is ready, then swap + revoke.
 *   - Blob URLs are always revoked on swap and on unmount to prevent leaks.
 */
function PdfPreview({ brand }: { brand: Marca }) {
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [firstRender, setFirstRender] = useState(true);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Monotonically-increasing request counter to detect stale async results. */
  const reqIdRef = useRef(0);
  /** Tracks the current active blob URL so it can be revoked on swap / unmount. */
  const currentUrlRef = useRef<string | null>(null);

  // Generate the PDF whenever a brand field that affects the theme changes.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      const reqId = ++reqIdRef.current;

      try {
        const theme = themeFromMarca(brand);

        const rawFotos = SAMPLE_INFORME.payload["panelFotografico"];
        const fotos = Array.isArray(rawFotos)
          ? (rawFotos as Array<{ url: string; titulo: string; descripcion: string }>)
          : [];

        // Dynamic import keeps pdfmake + vfs out of the initial bundle.
        const { renderInforme } = await import("@marco/pdf/browser");
        const bytes = await renderInforme(SAMPLE_INFORME, theme, fotos);

        // Discard stale results (a newer request already in flight).
        if (reqId !== reqIdRef.current) return;

        const newUrl = URL.createObjectURL(
          new Blob([bytes as unknown as BlobPart], { type: "application/pdf" }),
        );
        const oldUrl = currentUrlRef.current;
        currentUrlRef.current = newUrl;

        setIframeUrl(newUrl);
        setFirstRender(false);

        // Revoke the previous blob only after the new one is set.
        if (oldUrl) URL.revokeObjectURL(oldUrl);
      } catch {
        // Preview errors are non-fatal — hide the spinner but keep last PDF.
        if (reqId === reqIdRef.current) setFirstRender(false);
      }
    }, 400);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [
    brand.colorPrimario,
    brand.colorAcento,
    brand.logoUrl,
    brand.logoUrlClaro,
    brand.nombreComercial,
    brand.tagline,
    brand.direccion,
    brand.telefonos,
    brand.correo,
    brand.sitioWeb,
  ]);

  // Revoke blob URL on unmount to prevent memory leaks.
  useEffect(() => {
    return () => {
      if (currentUrlRef.current) {
        URL.revokeObjectURL(currentUrlRef.current);
        currentUrlRef.current = null;
      }
    };
  }, []);

  if (firstRender) {
    return (
      <div className="h-[60vh] w-full sm:h-[75vh] rounded-lg border border-[#ddd9cc] shadow-[0_10px_30px_rgba(0,0,0,0.05)] bg-white flex items-center justify-center">
        <div className="flex items-center gap-3 text-sm text-[#76746a]">
          <Loader2 className="size-5 animate-spin" aria-hidden />
          Generando vista previa…
        </div>
      </div>
    );
  }

  if (!iframeUrl) return null;

  // Desktop: inline iframe. Mobile browsers render blob/PDF URLs as a black box,
  // so we show a card that opens the PDF in the native viewer instead — same
  // pattern as InformeDetalle's PdfPreview.
  return (
    <>
      <iframe
        src={iframeUrl}
        title="Vista previa del documento PDF"
        className="hidden h-[60vh] w-full rounded-lg border border-[#ddd9cc] shadow-[0_10px_30px_rgba(0,0,0,0.05)] sm:block sm:h-[75vh]"
      />
      <div className="flex h-48 flex-col items-center justify-center gap-3 rounded-lg border border-[#ddd9cc] bg-white px-6 text-center shadow-[0_10px_30px_rgba(0,0,0,0.05)] sm:hidden">
        <ExternalLink className="size-6 text-[#76746a]" aria-hidden />
        <p className="text-sm text-[#76746a]">Vista previa disponible al abrir</p>
        <a
          href={iframeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            buttonVariants({ variant: "outline", size: "sm" }),
            "w-full justify-center gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.1em]",
          )}
        >
          <ExternalLink className="size-3.5" aria-hidden />
          Abrir PDF
        </a>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────

export function MarcaPage() {
  const [brand, setBrand] = useState<Marca>(BRAND_VACIO);
  const [original, setOriginal] = useState<Marca>(BRAND_VACIO);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    let vivo = true;
    getMarcaForEdit()
      .then((m) => {
        if (vivo) {
          const merged = { ...BRAND_VACIO, ...m };
          setBrand(merged);
          setOriginal(merged);
        }
      })
      .catch((err) => {
        if (vivo) toast.error(friendlyMessage(err));
      })
      .finally(() => {
        if (vivo) setCargando(false);
      });
    return () => {
      vivo = false;
    };
  }, []);

  function set<K extends keyof Marca>(key: K, value: Marca[K]) {
    setBrand((prev) => ({ ...prev, [key]: value }));
  }

  async function onGuardar() {
    setGuardando(true);
    try {
      const actualizada = await updateMarca(brand);
      const merged = { ...BRAND_VACIO, ...actualizada };
      setBrand(merged);
      setOriginal(merged);
      applyBrandTheme(actualizada);
      toast.success("Marca guardada");
    } catch (err) {
      toast.error(friendlyMessage(err));
    } finally {
      setGuardando(false);
    }
  }

  function onDescartar() {
    setBrand(original);
  }

  if (cargando) {
    return (
      <div className="flex h-full items-center justify-center p-12">
        <div className="flex items-center gap-3 text-sm text-[#76746a]">
          <Loader2 className="size-5 animate-spin" aria-hidden />
          Cargando configuración de marca…
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-full bg-[#f4f3ee] px-4 py-4 sm:px-8 sm:py-8 flex justify-center">
      <div className="w-full max-w-[1760px] flex flex-col md:flex-row gap-6 items-start">
        {/* ── Left column: form card ──────────────────────────────────── */}
        <div className="w-full md:w-5/12 bg-white rounded-[24px] border border-[#ddd9cc] shadow-sm p-8 flex flex-col">
          {/* Header */}
          <div className="mb-10">
            <h1 className="text-[#1a1a18] text-[1.75rem] font-bold leading-tight mb-2">
              Configuración de Marca
            </h1>
            <p className="text-[#76746a] text-sm leading-relaxed">
              Personalice la apariencia de sus reportes generados. Estos detalles aparecerán en las
              cabeceras de los PDFs.
            </p>
          </div>

          {/* Section 1 — Company */}
          <div className="space-y-6 mb-8">
            <UnderlineInput
              id="b-nombre"
              label="Nombre de la Empresa"
              value={brand.nombreComercial}
              onChange={(v) => set("nombreComercial", v)}
              placeholder="Ej. ACME Corp"
            />
            <UnderlineInput
              id="b-tagline"
              label="Lema / Tagline"
              value={brand.tagline}
              onChange={(v) => set("tagline", v)}
              placeholder="Construyendo el futuro"
            />
          </div>

          {/* Section 2 — Contact */}
          <div className="space-y-4 pt-2 mb-8">
            <span className={SECTION_LABEL}>Información de Contacto</span>
            <UnderlineInput
              id="b-direccion"
              label="Dirección"
              value={brand.direccion}
              onChange={(v) => set("direccion", v)}
              placeholder="Av. Industrial 123, Lima"
            />
            <UnderlineInput
              id="b-telefonos"
              label="Teléfonos"
              value={brand.telefonos}
              onChange={(v) => set("telefonos", v)}
              placeholder="+51 1 234 5678"
            />
            <UnderlineInput
              id="b-correo"
              label="Correo"
              type="email"
              value={brand.correo}
              onChange={(v) => set("correo", v)}
              placeholder="contacto@empresa.com"
            />
            <UnderlineInput
              id="b-web"
              label="Sitio Web"
              value={brand.sitioWeb}
              onChange={(v) => set("sitioWeb", v)}
              placeholder="www.empresa.com"
            />
          </div>

          {/* Section 3 — Branding */}
          <div className="space-y-4 pt-2 ">
            {/* Colors */}
            <div>
              <span className={SECTION_LABEL}>Color Principal</span>
              <div className="flex items-start gap-6">
                <ColorSwatch
                  id="b-primario"
                  label="Primario"
                  value={brand.colorPrimario}
                  onChange={(v) => set("colorPrimario", v)}
                  primary
                />
                <ColorSwatch
                  id="b-acento"
                  label="Acento"
                  value={brand.colorAcento}
                  onChange={(v) => set("colorAcento", v)}
                />
              </div>
            </div>

            {/* Logo para fondo oscuro (portada, cabecera) — normalmente claro/blanco */}
            <div>
              <span className={SECTION_LABEL}>Logo para fondo oscuro</span>
              <LogoField value={brand.logoUrl} onChange={(v) => set("logoUrl", v)} />
            </div>

            {/* Logo para fondo claro (firma) — normalmente oscuro */}
            <div>
              <span className={SECTION_LABEL}>Logo para fondo claro</span>
              <LogoField value={brand.logoUrlClaro} onChange={(v) => set("logoUrlClaro", v)} />
            </div>
          </div>

          {/* Actions */}
          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={onGuardar}
              disabled={guardando}
              className="flex flex-1 items-center justify-center gap-2 rounded-[20px] bg-[#1a1a18] px-6 py-3 text-sm font-bold text-white shadow-sm transition-all hover:shadow-md hover:bg-[#2a2a26] disabled:opacity-60 disabled:pointer-events-none"
            >
              {guardando ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <Save className="size-4" aria-hidden />
              )}
              Guardar Cambios
            </button>
            <button
              type="button"
              onClick={onDescartar}
              disabled={guardando}
              className="rounded-[20px] border border-[#ddd9cc] bg-transparent px-6 py-3 text-sm font-bold text-[#1a1a18] transition-colors hover:bg-[#f4f3ee] disabled:opacity-60 disabled:pointer-events-none flex-1"
            >
              Descartar
            </button>
          </div>
        </div>

        {/* ── Right column: document preview ─────────────────────────── */}
        <div className="w-full md:w-7/12 md:sticky md:top-8 flex flex-col gap-4">
          <h2 className="font-mono text-[0.6875rem] font-bold uppercase tracking-[0.15em] text-[#76746a] ml-1">
            Vista Previa del Documento
          </h2>
          <PdfPreview brand={brand} />
          {/* Info box */}
          <div className="flex items-start gap-3 rounded-xl border border-[#e5e2e1] bg-[#ebe7e6] p-4">
            <Info className="size-5 text-[#76746a] mt-0.5 shrink-0" aria-hidden />
            <p className="text-sm text-[#76746a] leading-snug">
              Esta vista previa es aproximada. El diseño final del PDF puede variar ligeramente
              dependiendo de la cantidad de datos en el reporte actual.
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}
